import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from "jwt-decode";
import setupSockets, { getSocketStatus } from '../socket/socketSetup';
import { setLogout } from '../state';
import store from '../state';
import * as connectionService from '../services/connectionService';

// Define a flag to disable notification features - must match the one in other files
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

// Function to show a session expired notification
const showSessionExpiredNotification = () => {
  // Create and show a notification element
  const notificationContainer = document.createElement('div');
  notificationContainer.className = 'fixed top-4 right-4 bg-amber-600 text-white p-4 rounded shadow-lg z-50 max-w-md';
  notificationContainer.innerHTML = `
    <div class="flex items-start">
      <div class="ml-3">
        <h3 class="text-sm font-medium">Session Expired</h3>
        <div class="mt-1 text-sm">
          <p>Your login session has expired. Please log in again to continue.</p>
        </div>
        <div class="mt-3">
          <button type="button" class="dismiss-btn text-sm bg-amber-700 px-2 py-1 rounded">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(notificationContainer);
  
  // Add event listener
  const dismissBtn = notificationContainer.querySelector('.dismiss-btn');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      notificationContainer.remove();
    });
  }
  
  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (document.body.contains(notificationContainer)) {
      notificationContainer.remove();
    }
  }, 5000);
};

const AuthVerifier = () => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const authToken = auth?.token;
  const userId = auth?.user?.id;
  
  // Since this component is only rendered for authenticated routes, we can assume auth is valid
  const socketInitializedRef = useRef(false);
  const cleanupFnRef = useRef(null);
  const [socketError, setSocketError] = useState(false);
  
  // Function to check token expiration
  const checkTokenExpiration = useCallback(() => {
    if (!authToken) return;
    
    try {
      const decodedToken = jwtDecode(authToken);
      const currentTime = Date.now() / 1000;
      
      if (decodedToken.exp < currentTime) {
        dispatch(setLogout());
      }
    } catch (error) {
      dispatch(setLogout());
    }
  }, [authToken, dispatch]);
  
  // Check socket status periodically
  useEffect(() => {
    // Skip socket checking if notifications are disabled
    if (!NOTIFICATIONS_ENABLED) {
      return;
    }
    
    // Check socket status every 5 seconds
    const socketCheckInterval = setInterval(() => {
      if (socketInitializedRef.current) {
        const status = getSocketStatus();
        const connected = status.connected;
        
        // If sockets were connected but now they're disconnected
        if (!connected && !socketError) {
          setSocketError(true);
        }
      }
    }, 5000);
    
    return () => {
      clearInterval(socketCheckInterval);
    };
  }, [socketError]);
  
  // Check token expiration periodically
  useEffect(() => {
    // Initial check
    checkTokenExpiration();
    
    // Check token every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    
    return () => {
      clearInterval(interval);
    };
  }, [checkTokenExpiration]);
  
  // Initialize socket connection on mount
  useEffect(() => {
    // Skip socket initialization if notifications are disabled
    if (!NOTIFICATIONS_ENABLED) {
      return;
    }
    
    // Only attempt to initialize if we have a valid user ID
    if (userId) {
      // First, make sure any existing socket connections are properly cleaned up
      if (cleanupFnRef.current) {
        try {
          cleanupFnRef.current();
        } catch (error) {
          // Handle error silently
        }
        cleanupFnRef.current = null;
        socketInitializedRef.current = false;
      }
      
      // Clear any existing errors
      setSocketError(false);
      
      // Initialize the WebSocket connection with the store
      try {
        cleanupFnRef.current = setupSockets(store);
        socketInitializedRef.current = true;
        
        // Add event listener for socket connection
        const handleConnect = () => {
          setSocketError(false);
        };
        
        // Add event listener for socket disconnection
        const handleDisconnect = () => {
          // Don't immediately set error - reconnect logic is handled in socketSetup.js
        };
        
        window.addEventListener('socket-connected', handleConnect);
        window.addEventListener('socket-disconnected', handleDisconnect);
        
        // Clean up event listeners and socket when component unmounts or userId changes
        return () => {
          window.removeEventListener('socket-connected', handleConnect);
          window.removeEventListener('socket-disconnected', handleDisconnect);
          
          // Clean up socket connection
          if (cleanupFnRef.current) {
            try {
              cleanupFnRef.current();
            } catch (error) {
              // Handle error silently
            }
            cleanupFnRef.current = null;
            socketInitializedRef.current = false;
          }
        };
      } catch (error) {
        setSocketError(true);
        socketInitializedRef.current = false;
        cleanupFnRef.current = null;
      }
    }
  }, [userId]); // Only depend on userId to prevent reconnections on every render
  
  // Render nothing - this component just handles auth verification and sockets
  return null;
};

export default AuthVerifier;